// ── 재고 알림·발주 추천 공통 유틸 ─────────────────────────────────
// InventoryManager + App.js (글로벌 알림 배지) 공유

// ── 최근 N개월 발주 내역에서 식재료별 평균 사용량
export function calcAvgUsage(orderHistory, months = 3) {
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  const cutoffStr = cutoff.toISOString().slice(0, 7);

  const usage = {};
  (orderHistory || []).forEach(entry => {
    if (!entry.date || entry.date.slice(0, 7) < cutoffStr) return;
    (entry.items || []).forEach(item => {
      if (!usage[item.name]) usage[item.name] = { totalQty: 0, monthsSet: new Set(), unit: item.unit || '' };
      usage[item.name].totalQty += Number(item.qty) || 0;
      usage[item.name].monthsSet.add(entry.date.slice(0, 7));
    });
  });

  const result = {};
  Object.entries(usage).forEach(([name, data]) => {
    result[name] = {
      avgMonthly: data.totalQty / months,
      avgDaily: data.totalQty / (months * 30),
      unit: data.unit,
      dataMonths: data.monthsSet.size,
      source: 'orderHistory',
    };
  });
  return result;
}

// ── 두 사용량 데이터 병합 (orderHistory + recipe-based)
// 정책: 둘 다 있으면 큰 값 사용 (보수적), 한쪽만 있으면 그대로
export function mergeUsage(usageA, usageB) {
  const result = {};
  const allKeys = new Set([...Object.keys(usageA || {}), ...Object.keys(usageB || {})]);
  for (const name of allKeys) {
    const a = usageA?.[name];
    const b = usageB?.[name];
    if (a && b) {
      result[name] = {
        avgMonthly: Math.max(a.avgMonthly, b.avgMonthly),
        avgDaily: Math.max(a.avgDaily, b.avgDaily),
        unit: a.unit || b.unit,
        dataMonths: a.dataMonths || 0,
        source: a.avgMonthly >= b.avgMonthly ? a.source : b.source,
        sources: [a.source, b.source].filter(Boolean),
      };
    } else if (a) {
      result[name] = { ...a, sources: [a.source] };
    } else if (b) {
      result[name] = { ...b, sources: [b.source] };
    }
  }
  return result;
}

// ── 재고 상태 계산
export function getStatus(currentStock, safetyStock, avgDaily) {
  if (currentStock <= 0) return { label: '재고 없음', color: '#52525B', bg: '#F4F4F5', icon: '🚨' };
  if (avgDaily <= 0) return { label: '데이터 없음', color: '#9ca3af', bg: '#f3f4f6', icon: '❓' };
  const daysLeft = currentStock / avgDaily;
  if (currentStock <= safetyStock) return { label: '발주 필요', color: '#52525B', bg: '#F4F4F5', icon: '🔴', daysLeft };
  if (daysLeft <= 7)  return { label: `${Math.floor(daysLeft)}일 후 소진`, color: '#EA580C', bg: '#F4F4F5', icon: '⚠️', daysLeft };
  if (daysLeft <= 14) return { label: `${Math.floor(daysLeft)}일 남음`,    color: '#EA580C', bg: '#FAFAFA', icon: '🟡', daysLeft };
  return { label: `${Math.floor(daysLeft)}일 남음`, color: '#18181B', bg: '#FAFAFA', icon: '✅', daysLeft };
}

// ── 적정 발주량 = 평균 월 사용량 × 1.5 - 현재 재고
export function calcRecommendedOrder(item) {
  return Math.max(0, Math.ceil((item.avgMonthly || 0) * 1.5 - (item.currentStock ?? 0)));
}

// ── 발주 추천 리스트 (Dashboard 카드용) ─────────────
// 발주 필요 식재료를 우선순위로 정렬해 반환.
// 정렬: 재고 없음 > 일 남음 적은 순 > 평균 월 사용량 큰 순
export function getOrderRecommendations(prices, inventory, orderHistory, externalUsage, options = {}) {
  const { limit = 5 } = options;
  const usage = externalUsage || calcAvgUsage(orderHistory, 3);
  const inv = inventory?.items || {};

  const priceMap = {};
  (prices || []).forEach(p => { if (p.name) priceMap[p.name] = p; });

  const allNames = new Set([
    ...(prices || []).map(p => p.name),
    ...Object.keys(usage),
  ]);

  const recommendations = [];
  for (const name of allNames) {
    if (!name) continue;
    const u = usage[name] || { avgMonthly: 0, avgDaily: 0, unit: '' };
    const stockEntry = inv[name] || {};
    const currentStock = stockEntry.currentStock ?? null;
    const safetyStock = stockEntry.safetyStock ?? 0;
    if (currentStock === null) continue;

    const status = getStatus(currentStock, safetyStock, u.avgDaily);
    if (!['🚨', '🔴', '⚠️'].includes(status.icon)) continue;

    const recommendedQty = calcRecommendedOrder({
      avgMonthly: u.avgMonthly,
      currentStock,
    });
    if (recommendedQty <= 0) continue;

    const priceInfo = priceMap[name] || {};
    const unitPrice = Number(priceInfo.unitPrice) || Number(priceInfo.price) || 0;
    const estimatedCost = unitPrice > 0 ? unitPrice * recommendedQty : 0;

    recommendations.push({
      name,
      currentStock,
      safetyStock,
      avgDaily: u.avgDaily,
      avgMonthly: u.avgMonthly,
      daysLeft: status.daysLeft ?? 0,
      status,
      recommendedQty,
      unit: u.unit || priceInfo.unit || '',
      unitPrice,
      estimatedCost,
      urgency: status.icon === '🚨' ? 3 : status.icon === '🔴' ? 2 : 1,
    });
  }

  recommendations.sort((a, b) => {
    if (a.urgency !== b.urgency) return b.urgency - a.urgency;
    if (a.daysLeft !== b.daysLeft) return a.daysLeft - b.daysLeft;
    return b.avgMonthly - a.avgMonthly;
  });

  return recommendations.slice(0, limit);
}

// ── 발주 필요 항목 수 (글로벌 알림 배지용)
// usage가 외부에서 계산되어 옵션으로 전달됨 (recipe-based 등 합치려면)
export function countNeedOrder(prices, inventory, orderHistory, externalUsage) {
  const usage = externalUsage || calcAvgUsage(orderHistory, 3);
  const inv = inventory?.items || {};

  const allNames = new Set([
    ...prices.map(p => p.name),
    ...Object.keys(usage),
  ]);

  let count = 0;
  for (const name of allNames) {
    const u = usage[name] || { avgMonthly: 0, avgDaily: 0 };
    const stockEntry = inv[name] || {};
    const currentStock = stockEntry.currentStock ?? null;
    const safetyStock = stockEntry.safetyStock ?? 0;
    if (currentStock === null) continue;  // 미입력 항목은 알림 제외 (false-positive 방지)

    const status = getStatus(currentStock, safetyStock, u.avgDaily);
    if (status.icon === '🔴' || status.icon === '🚨' || status.icon === '⚠️') {
      count++;
    }
  }
  return count;
}
